import { type Dispatch, type SetStateAction, useCallback, useState } from 'react';

type UseBooleanReturnValue<T> = {
  value: T;
  setValue: Dispatch<SetStateAction<T>>;
  setTrue: () => void;
  setFalse: () => void;
  toggle: () => void;
};

type UseBoolean = {
  (): UseBooleanReturnValue<boolean>;
  <T extends boolean>(initialValue: T): UseBooleanReturnValue<boolean>;
  <T extends undefined>(initialValue: T): UseBooleanReturnValue<boolean | undefined>;
  <T extends boolean | undefined>(initialValue: T): UseBooleanReturnValue<boolean | undefined>;
};

const useBoolean: UseBoolean = <T extends boolean | undefined>(...params: [T] | []) => {
  const initialValue = params.length === 1 ? params[0] : false;
  const [value, setValue] = useState(initialValue);

  const setTrue = useCallback(() => setValue(true), []);
  const setFalse = useCallback(() => setValue(false), []);
  const toggle = useCallback(() => setValue((x) => !x), []);

  return { value, setValue, setTrue, setFalse, toggle };
};

export default useBoolean;
